import { configureStore } from "@reduxjs/toolkit";
import { type InitialisedFirebase } from "igfd-commons";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistStore,
} from "redux-persist";

import type { AxiosInstances } from "igfd-commons/src/http";

import { type ThunkExtraArgument } from "../types/thunk";
import { FirestoreBackedDishApi } from "./backendApis/dishApi";
import { FirestoreBackedDishViewedApi } from "./backendApis/dishViewedApi";
import { FirestoreBackedLocationApi } from "./backendApis/locationApi";
import { FirestoreBackedMenuApi } from "./backendApis/menuApi";
import { FirestoreBackedMenuFavoriteApi } from "./backendApis/menuFavoritesApi";
import { FirestoreBackedMenuUrlV2Api } from "./backendApis/menuUrlV2Api";
import { FirestoreBackedProductApi } from "./backendApis/productApi";
import { FirestoreBackedProductUploadApi } from "./backendApis/productUploadApi";
import { ResolvedEdiblesBackend } from "./backendApis/resolvedEdiblesApi";
import { FirestoreBackedRestaurantApi } from "./backendApis/restaurantApi";
import { FirestoreBackedSupplierApi } from "./backendApis/supplierApi";
import { FirestoreBackedThemeApi } from "./backendApis/themeApi";
import { FirestoreBackedUserApi } from "./backendApis/userApi";
import { rootReducer } from "./reducers/rootReducer";

const reduxPersistActions = [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER];

export function storeWithThunkExtraArgs(
  initialisedFirebase: InitialisedFirebase,
  axiosInstances: AxiosInstances
) {
  const { firestoreRefs } = initialisedFirebase;
  const menuApi = new FirestoreBackedMenuApi(firestoreRefs);
  const dishApi = new FirestoreBackedDishApi({
    firestoreRefs,
    // create a new closure here to pass the complete MenuApi as an object to the
    // callback from the dish Api.
    beforeDeleteDishFromMenuSection: (transaction, restaurantUuid, dishId) =>
      menuApi.deleteDishFromMenuSections(transaction, restaurantUuid, dishId),
  });
  const productApi = new FirestoreBackedProductApi({ firestoreRefs });
  const productUploadApi = new FirestoreBackedProductUploadApi({
    firestoreRefs,
  });
  const resolvedEdiblesApi = new ResolvedEdiblesBackend({
    axiosClientWithoutAuth: axiosInstances.clientWithoutAuth,
  });
  const restaurantApi = new FirestoreBackedRestaurantApi(firestoreRefs);
  const locationApi = new FirestoreBackedLocationApi(firestoreRefs);
  const dishViewedApi = new FirestoreBackedDishViewedApi(firestoreRefs);
  const menuFavoriteApi = new FirestoreBackedMenuFavoriteApi(firestoreRefs);
  const supplierApi = new FirestoreBackedSupplierApi({ firestoreRefs });
  const userApi = new FirestoreBackedUserApi(firestoreRefs);
  const menuUrlV2Api = new FirestoreBackedMenuUrlV2Api({ firestoreRefs });
  const themeApi = new FirestoreBackedThemeApi({ firestoreRefs });

  const store = configureStore({
    reducer: rootReducer({ usePersist: true }),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: {
          warnAfter: 128,
          ignoredPaths: [
            "dish.dishes",
            "product.products",
            "menu.menus",
            "menu.menuSectionsByMenuId",
            "resolvedEdibles.resolvedDishesById",
            "resolvedEdibles.resolvedProductsById",
            "restaurant.accessibleRestaurants",
            "location.locations",
            "menuFavorites.menuFavorites",
            "dishViewed.recentlyViewedDishes",
            "menuUrlV2.menuUrlsV2",
            "theme.themes",
          ],
        },
        serializableCheck: {
          // Actions from redux persist are not serializable.
          // However we do want to make sure our own actions are serializable.
          ignoredActions: reduxPersistActions,
        },
        thunk: {
          extraArgument: {
            dishApi,
            dishViewedApi,
            productApi,
            productUploadApi,
            resolvedEdiblesApi,
            menuApi,
            restaurantApi,
            locationApi,
            supplierApi,
            menuFavoriteApi,
            userApi,
            menuUrlV2Api,
            themeApi,
            axiosInstances,
          } as ThunkExtraArgument,
        },
      }),
  });

  const persistor = persistStore(store);

  return { store, persistor };
}
